'use client';
import { FieldReadFunction, makeVar } from '@apollo/client';
import currency from 'currency.js';

import {
  BundleProduct,
  GetSkuProductsDocument,
  GetSkuProductsQuery,
} from '@storefront/data-commerce';
import {
  ProductConfiguration,
  ProductOptions,
} from '@storefront/types/Product';

import {
  addLowestPriceToEachItem,
  calculateLowestPrice,
} from './pdp-config-state.utils';
import { BundleProductWithLowestPrice } from '../product-configuration/product-configuration.utils';
import { calculatePricesAndSetOptions } from '../utils/prices';
import { saveProductConfiguration } from '../utils/utils';

export const configurationState = makeVar<ProductConfiguration>({
  activeSku: '',
});

export const pdpConfigStateResolver: FieldReadFunction<any> = (
  _: any,
  { cache, variables }
) => {
  const { configuration, skuList } = variables as {
    configuration: ProductConfiguration;
    skuList: string[];
  };
  const data = cache.readQuery<GetSkuProductsQuery>({
    query: GetSkuProductsDocument,
    variables: {
      skuList: skuList,
    },
  });
  const multipleProduct = data?.products
    ?.items as BundleProductWithLowestPrice[];

  const activeSku = configuration.activeSku as string;
  const { prices, options } = calculatePricesAndSetOptions(
    multipleProduct,
    configuration,
    activeSku
  );
  const selected_options = options?.[activeSku] ?? [];

  const selectedProduct = multipleProduct?.find(
    (product) => product.sku === activeSku
  ) as BundleProduct;

  const lowestPrice = currency(selectedProduct?.lowest_price).value;
  // user didnt selected any option
  if (lowestPrice > prices.totalPricePerMonth) {
    prices.totalPricePerMonth = lowestPrice;
  }
  return {
    selected_options,
    prices,
  };
};

export const productsLowestPriceResolver: FieldReadFunction<any> = (
  cachedQuery: BundleProduct[]
) => {
  const items = cachedQuery.map(({ items, ...otherProps }) => {
    const lowest_price = calculateLowestPrice(items);
    return {
      ...otherProps,
      lowest_price,
      items: addLowestPriceToEachItem(items, lowest_price)?.map((item) => ({
        ...item,
        options: item.options?.map((option) => ({
          ...option,
          lowest_price: option?.lowestPrice,
        })),
      })),
    };
  });
  return items;
};

type UpdateConfigurations = {
  configuration: ProductConfiguration;
  activeSKU: string;
  uid: string;
  updateValue: string;
  requiredOption?: boolean;
};

export const updateConfigurations = ({
  configuration,
  activeSKU,
  uid,
  updateValue,
  requiredOption,
}: UpdateConfigurations) => {
  const configs = (configuration[activeSKU] ?? {}) as ProductOptions;

  let newConfig;

  if (requiredOption) {
    newConfig = {
      ...configuration,
      [activeSKU]: { ...configs, [uid]: updateValue },
    };
  } else {
    newConfig = {
      ...configuration,
      [activeSKU]: {
        ...configs,
        [uid]: updateValue,
        selectedPaperOption: true,
      },
    };
  }

  const newState = {
    ...newConfig,
    activeSku: activeSKU,
  };

  configurationState(newState);
  saveProductConfiguration(newState);
};

type Remove = {
  configuration: ProductConfiguration;
  activeSKU: string;
  uid: string;
  zeroDollarUid?: string;
};
export const removeOptions = ({
  configuration,
  activeSKU,
  uid,
  zeroDollarUid,
}: Remove) => {
  const configs = configuration[activeSKU] as ProductOptions;
  if (configs[uid]) {
    delete configs[uid];
  }
  if (configs['selectedPaperOption']) {
    delete configs['selectedPaperOption'];
  }
  if (zeroDollarUid) {
    /// add 0 $ plan to the configuration
    const newConfig = {
      ...configuration,
      [activeSKU]: {
        ...configs,
        [uid]: zeroDollarUid,
        selectedPaperOption: true,
      },
    };
    const newState = {
      ...newConfig,
      activeSku: activeSKU,
    };
    configurationState(newState);
    saveProductConfiguration(newState);
  } else {
    const newConfig = {
      ...configuration,
      [activeSKU]: { ...configs, selectedPaperOption: true },
    };
    const newState = {
      ...newConfig,
      activeSku: activeSKU,
    };
    configurationState(newState);
    saveProductConfiguration(newState);
  }
};
